import { useMemo } from "react";
import { createRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ClubGuard } from "@/components/ClubGuard";
import {
  GatheringForm,
  type GatheringFormInitialValues,
} from "@/components/gatherings/GatheringForm";
import { RsvpBreakdown } from "@/components/gatherings/RsvpBreakdown";
import { useClubChannel } from "@/lib/clubs/useClubChannel";
import {
  gatheringDetailKey,
  gatheringsListKey,
  useCancelGathering,
  useClubDetails,
  useGathering,
  useUpdateGathering,
  type GatheringDetail,
  type GatheringVenueType,
} from "@/lib/clubs/gatherings";
import { clubsRoute } from "./clubs";
import styles from "./clubs-gatherings-routes.module.scss";

function GatheringDetailRoute() {
  const params = useParams({ from: clubsGatheringsDetailRoute.id });
  const clubId = params.id;
  const gatheringId = params.gid;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: club } = useClubDetails(clubId);

  const { data: gathering, isLoading } = useGathering(clubId, gatheringId);
  const updateMutation = useUpdateGathering(clubId, gatheringId);
  const cancelMutation = useCancelGathering(clubId, gatheringId);

  useClubChannel(clubId, (event) => {
    if (
      event.type === "gathering:updated" ||
      event.type === "gathering:cancelled"
    ) {
      if (event.payload.gatheringId === gatheringId) {
        qc.invalidateQueries({
          queryKey: gatheringDetailKey(clubId, gatheringId),
        });
        qc.invalidateQueries({ queryKey: gatheringsListKey(clubId) });
      }
      return;
    }
    if (event.type === "gathering:rsvp_updated") {
      if (event.payload.gatheringId === gatheringId) {
        qc.invalidateQueries({
          queryKey: gatheringDetailKey(clubId, gatheringId),
        });
      }
    }
  });

  const initialValues = useMemo<
    Partial<GatheringFormInitialValues> | undefined
  >(() => (gathering ? toFormValues(gathering) : undefined), [gathering]);

  if (isLoading && !gathering) {
    return (
      <div role="status" aria-live="polite" style={{ padding: 32 }}>
        Loading…
      </div>
    );
  }

  if (!gathering) {
    return (
      <div className={styles.shell}>
        <p>Gathering not found.</p>
      </div>
    );
  }

  const cancelled = Boolean(gathering.cancelledAt);

  return (
    <div className={styles.shell}>
      <header className={styles.head}>
        <p className={styles.eyebrow}>{club?.name ?? "Club"} · Gathering</p>
        <h1 className={styles.title}>{gathering.title}</h1>
        <p className={styles.sub}>
          {cancelled
            ? "Cancelled. Edits are disabled."
            : "Edit details, see RSVPs, or cancel the gathering."}
        </p>
      </header>

      <RsvpBreakdown rsvps={gathering.rsvps} />

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Edit gathering</h2>
        {cancelled ? (
          <p className={styles.note}>
            This gathering has been cancelled and can&rsquo;t be edited.
          </p>
        ) : (
          <GatheringForm
            mode="edit"
            initialValues={initialValues}
            readingRooms={club?.readingRooms ?? []}
            isReadingRoomsLoading={!club}
            isSubmitting={updateMutation.isPending}
            isCancelling={cancelMutation.isPending}
            submitError={updateMutation.error ?? cancelMutation.error}
            clubDiscoverable={Boolean(gathering.clubDiscoverable)}
            onSubmit={(payload) => updateMutation.mutate(payload)}
            onDiscard={() =>
              navigate({
                to: "/clubs/$id/gatherings",
                params: { id: clubId },
              })
            }
            onCancelGathering={() =>
              cancelMutation.mutate(undefined, {
                onSuccess: () =>
                  navigate({
                    to: "/clubs/$id/gatherings",
                    params: { id: clubId },
                  }),
              })
            }
          />
        )}
      </section>
    </div>
  );
}

function toFormValues(g: GatheringDetail): Partial<GatheringFormInitialValues> {
  const start = new Date(g.date);
  const end = g.endDate ? new Date(g.endDate) : null;
  return {
    title: g.title,
    description: g.description ?? "",
    dateLocal: toLocalDate(start),
    timeLocal: toLocalTime(start),
    endTimeLocal: end ? toLocalTime(end) : "",
    venueType: (g.venueType as GatheringVenueType | null) ?? "",
    venueName: g.venueName ?? "",
    neighborhood: g.neighborhood ?? "",
    address: g.address ?? "",
    capacity: g.capacity != null ? String(g.capacity) : "",
    discussionFocus: g.discussionFocus ?? "",
    readingRoomLinkEnabled: Boolean(g.readingRoomId),
    readingRoomId: g.readingRoomId ?? "",
    publiclyVisible: Boolean(g.publiclyVisible),
  };
}

function pad(n: number) {
  return n.toString().padStart(2, "0");
}

function toLocalDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function toLocalTime(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export const clubsGatheringsDetailRoute = createRoute({
  getParentRoute: () => clubsRoute,
  path: "/$id/gatherings/$gid",
  component: () => (
    <ClubGuard requireClubId>
      <GatheringDetailRoute />
    </ClubGuard>
  ),
});
